import React, { useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import type { Option } from '../../search/searchTypes';
import { useComponentVisibleDropdown } from '../../utilities/display';
import styles from './Popout.module.css';

type Props = {
  readonly children: React.ReactNode;
  readonly buttonText?: string;
  readonly ariaLabel?: string;
  readonly onOpenChange?: (open: boolean) => void;
  readonly onReset?: () => void;
  readonly arrowIcon?: boolean;
  readonly clearIcon?: boolean;
  readonly selectedOptions?:
    | number
    | Option<string | number>[]
    | Option<string | number>
    | null;
  readonly maxDisplayOptions?: number;
  readonly displayOptionLabel?: boolean;
  readonly className?: string;
  readonly wrapperClassName?: string;
  readonly dropdownClassName?: string;
  readonly notifications?: number;
  readonly colors?: { [optionValue: string]: string };
  readonly dataTutorial?: number;
  readonly Icon?: React.JSX.Element;
  readonly tooltipText?: string;
  readonly fullWidth?: boolean;
};

function getText(
  selectedOptions: Props['selectedOptions'],
  maxDisplayOptions: number,
  displayOptionLabel: boolean | undefined,
  colors: { [optionValue: string]: string } | undefined,
): undefined | string | React.JSX.Element[] {
  if (!selectedOptions) return undefined;
  if (Array.isArray(selectedOptions)) {
    if (selectedOptions.length === 0) return undefined;
    const topOptions = selectedOptions.slice(0, maxDisplayOptions);
    const text = topOptions.map((option, index) => {
      const optionLabel = displayOptionLabel ? option.label : option.value;
      const color = colors?.[option.value];
      const span = color ? (
        <span style={{ color }}>{optionLabel}</span>
      ) : (
        optionLabel
      );
      if (index < topOptions.length - 1)
        return <React.Fragment key={index}>{span}, </React.Fragment>;
      if (
        index === topOptions.length - 1 &&
        selectedOptions.length > maxDisplayOptions
      ) {
        return (
          <React.Fragment key={index}>
            {span} + {selectedOptions.length - maxDisplayOptions}
          </React.Fragment>
        );
      }
      return <React.Fragment key={index}>{span}</React.Fragment>;
    });
    return text;
  } else if (typeof selectedOptions === 'object') {
    return displayOptionLabel
      ? selectedOptions.label
      : String(selectedOptions.value);
  }
  return selectedOptions !== 0 ? `Advanced: ${selectedOptions}` : undefined;
}

function NotificationIcon({ count }: { readonly count: number }) {
  return (
    <div className={styles.notificationIcon}>
      <span>{count}</span>
    </div>
  );
}

export function Popout({
  children,
  buttonText,
  ariaLabel,
  onOpenChange,
  onReset,
  arrowIcon = true,
  clearIcon = true,
  selectedOptions,
  maxDisplayOptions = 3,
  displayOptionLabel,
  className,
  wrapperClassName,
  dropdownClassName,
  notifications,
  colors,
  dataTutorial,
  Icon,
  tooltipText,
  fullWidth,
}: Props) {
  const tooltipId = useId();
  // Ref to detect outside clicks for popout button and dropdown
  const { toggleRef, dropdownRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisibleDropdown<HTMLButtonElement, HTMLDivElement>(false);

  useEffect(() => {
    onOpenChange?.(isComponentVisible);
  }, [isComponentVisible, onOpenChange]);

  const text = getText(
    selectedOptions,
    maxDisplayOptions,
    displayOptionLabel,
    colors,
  );

  // Popout button styles for open and active states
  const buttonStyles = (open: boolean) => {
    if (open) {
      return {
        backgroundColor: 'var(--color-surface-active)',
        color: 'var(--color-primary)',
      };
    }
    if (text) {
      return {
        color: 'var(--color-primary)',
      };
    }
    return undefined;
  };

  const ArrowIcon = isComponentVisible ? IoMdArrowDropdown : IoMdArrowDropup;
  const [dropdownXOffset, setDropdownXOffset] = useState(0);

  useEffect(() => {
    // Avoid the dropdown going out of the viewport. By default it's left-
    // aligned with the trigger button, but we may have to left-shift it.
    // Note: we only reposition the dropdown once when it becomes visible.
    // this is on purpose: when resizing the window, the resize event fires
    // before reflow happens, so the dropdown tends to flicker and become
    // unstable.
    if (!isComponentVisible) {
      setDropdownXOffset(0);
      return;
    }
    if (!dropdownRef.current) return;
    // Offset is always 0 on first paint after opening (reset above on close).
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    if (dropdownRect.right > window.innerWidth) {
      setDropdownXOffset(
        Math.max(-dropdownRect.left, window.innerWidth - dropdownRect.right),
      );
    } else {
      setDropdownXOffset(0);
    }
  }, [isComponentVisible, dropdownRef]);

  const triggerButton = (
    <button
      type="button"
      onClick={() => setIsComponentVisible(!isComponentVisible)}
      style={buttonStyles(isComponentVisible)}
      ref={toggleRef}
      className={clsx(
        className,
        styles.button,
        fullWidth && styles.buttonFullWidth,
      )}
      data-tutorial={dataTutorial ? `catalog-${dataTutorial}` : ''}
      aria-label={ariaLabel}
    >
      {Icon ?? null}
      {text || buttonText ? <div>{text ?? buttonText}</div> : null}

      {text && clearIcon ? (
        <IoClose
          className={styles.clearIcon}
          onClick={(e) => {
            // Prevent parent popout onClick from toggling the dropdown
            e.stopPropagation();
            onReset?.();
          }}
        />
      ) : arrowIcon ? (
        <ArrowIcon className={styles.arrowIcon} />
      ) : null}
      {notifications ? <NotificationIcon count={notifications} /> : null}
    </button>
  );

  return (
    <div
      data-tutorial={
        dataTutorial ? `catalog-${dataTutorial}-observe` : undefined
      }
      className={clsx(
        styles.wrapper,
        fullWidth && styles.wrapperFullWidth,
        wrapperClassName,
      )}
    >
      {tooltipText ? (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
        >
          {triggerButton}
        </OverlayTrigger>
      ) : (
        triggerButton
      )}
      {isComponentVisible ? (
        <div
          className={clsx(
            styles.dropdown,
            fullWidth && styles.dropdownFullWidth,
            dropdownClassName,
          )}
          ref={dropdownRef}
          style={
            dropdownXOffset
              ? { transform: `translateX(${dropdownXOffset}px)` }
              : undefined
          }
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
